import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"

import {
  emptyContact,
  emptyContactBoat,
} from "src/components/ContactBoatPicker/emptyShapes"
import parseResolveError from "src/components/ContactBoatPicker/parseResolveError"
import useContactBoatPicker, {
  PANEL_ARCHIVED,
  PANEL_NONE,
} from "src/components/ContactBoatPicker/useContactBoatPicker"
import Modal from "src/components/Modal"

import { marinaCreateLongtermWaitlistEntry } from "src/api/Waitlist/longTerm/entries"

import { useToast } from "src/hooks/use_toast"

import { getLongtermWaitlistEntriesKey } from "../helpers"
import { LongTermWaitlistContext } from "./LongTermWaitlistContextProvider"
import MarinaEntryForm from "./WaitlistForm/MarinaEntryForm"

const CreateEntryModal = ({ onClose }) => {
  const form = useForm({
    defaultValues: {
      boater_notification: true,
      contact: emptyContact,
      contact_id: "",
      contact_boat: emptyContactBoat,
      contact_boat_id: "",
      note: "",
    },
  })

  return (
    <Modal onClose={onClose} isOpen>
      <Modal.Header title="Add Boater to Waitlist" />
      <FormProvider {...form}>
        <CreateEntryModalBody onClose={onClose} />
      </FormProvider>
    </Modal>
  )
}

const CreateEntryModalBody = ({ onClose }) => {
  const { handleSubmit, watch } = useFormContext()
  const picker = useContactBoatPicker({ emptyContact, emptyContactBoat })
  const [pickerSubmitError, setPickerSubmitError] = useState(null)
  const [contactId, contactBoatId] = watch(["contact_id", "contact_boat_id"])
  const showToast = useToast()
  const queryClient = useQueryClient()
  const { trackEvent, navigateToView, incrementViewCount } = useContext(
    LongTermWaitlistContext
  )

  useEffect(() => {
    trackEvent("long_term_waitlist_marina:new_entry_form_modal_viewed")
  }, [trackEvent])

  const createEntryMutation = useMutation({
    mutationKey: ["longTermWaitlistEntry", "create"],
    mutationFn: marinaCreateLongtermWaitlistEntry,
    onSuccess: ({ entry }) => {
      trackEvent(
        "long_term_waitlist_marina:new_entry_modal_submission_pressed",
        { group_id: entry.groupId }
      )

      queryClient.invalidateQueries(
        getLongtermWaitlistEntriesKey(entry.groupId)
      )
      if (entry.groupId == null) {
        incrementViewCount("ungrouped_entries")
        navigateToView("view", "ungrouped_entries")
      } else {
        navigateToView("group", entry.groupId)
      }

      showToast(`Entry created for ${entry.contactName}.`, { type: "success" })
      onClose()
    },
  })

  const onSubmit = async (formData) => {
    setPickerSubmitError(null)
    try {
      const { contactId, contactBoatId } =
        await picker.resolveSelection(formData)
      createEntryMutation.mutate({
        contact_id: contactId,
        contact_boat_id: contactBoatId,
        group: formData.group,
        special_request: formData.special_request,
        note: formData.note,
        boater_notification: formData.boater_notification,
      })
    } catch (error) {
      const parsed = parseResolveError(error)
      if (parsed) setPickerSubmitError(parsed)
    }
  }

  const submitDisabled =
    picker.isWorking ||
    createEntryMutation.isLoading ||
    picker.panel === PANEL_ARCHIVED ||
    (picker.panel === PANEL_NONE && (!contactId || !contactBoatId))

  return (
    <>
      <Modal.Body>
        <MarinaEntryForm
          picker={picker}
          pickerSubmitError={pickerSubmitError}
          mutationError={createEntryMutation.error}
        />
      </Modal.Body>
      <Modal.Footer
        onClose={onClose}
        onSubmit={handleSubmit(onSubmit)}
        confirmBtnText="Add to Waitlist"
        confirmBtnLoading={createEntryMutation.isLoading || picker.isWorking}
        disabled={submitDisabled}
      />
    </>
  )
}

CreateEntryModalBody.propTypes = {
  onClose: PropTypes.func.isRequired,
}

CreateEntryModal.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default CreateEntryModal
